import type { Match, PadelMatchResult, Team } from "@/types";

/**
 * Format a Padel match result as a string
 * - Prefixes with set count (e.g., "2-1 (6-2, 4-6, 6-3)")
 * - Shows tie-breaks in format 6-7 [4-7]
 * @param result - The padel match result
 * @returns Formatted result string
 */
export function padelMatchResultToString(result: PadelMatchResult): string {
  if (result.walkover) return "WO";

  const sets: string[] = [];
  let team1Sets = 0;
  let team2Sets = 0;

  const formatSet = (
    team1Games: number,
    team2Games: number,
    tieBreak?: { team1Points: number; team2Points: number },
    _isTieBreakOnly?: boolean,
  ): string => {
    let setStr = `${team1Games}-${team2Games}`;
    if (tieBreak) {
      setStr += ` [${tieBreak.team1Points}-${tieBreak.team2Points}]`;
    }
    return setStr;
  };

  const countSet = (s: { team1Games: number; team2Games: number }) => {
    if (s.team1Games > s.team2Games) team1Sets++;
    else if (s.team2Games > s.team1Games) team2Sets++;
  };

  sets.push(
    formatSet(
      result.set1.team1Games,
      result.set1.team2Games,
      result.set1.tieBreak,
      result.set1.isTieBreakOnly,
    ),
  );
  countSet(result.set1);

  sets.push(
    formatSet(
      result.set2.team1Games,
      result.set2.team2Games,
      result.set2.tieBreak,
      result.set2.isTieBreakOnly,
    ),
  );
  countSet(result.set2);

  const set3Played = result.set3.team1Games > 0 || result.set3.team2Games > 0;

  if (set3Played) {
    sets.push(
      formatSet(
        result.set3.team1Games,
        result.set3.team2Games,
        result.set3.tieBreak,
        result.set3.isTieBreakOnly,
      ),
    );
    countSet(result.set3);
  }

  return `${team1Sets}-${team2Sets} (${sets.join(", ")})`;
}

/**
 * Detect winner for a Padel match based on sets won
 * @param result - The padel match result
 * @returns winner team ID.
 */
export function detectPadelWinner(
  match: Match,
  result: PadelMatchResult,
): Team["id"] | null {
  // Walkover matches have a pre-determined winner
  if (result?.walkover) return result.winnerId ?? null;

  // Count sets won by each team
  let team1SetsWon = 0;
  let team2SetsWon = 0;

  if (!result || !result.set1 || !result.set2) {
    return null;
  }

  // Check set 1
  if (result.set1.team1Games > result.set1.team2Games) {
    team1SetsWon++;
  } else if (result.set1.team2Games > result.set1.team1Games) {
    team2SetsWon++;
  }

  // Check set 2
  if (result.set2.team1Games > result.set2.team2Games) {
    team1SetsWon++;
  } else if (result.set2.team2Games > result.set2.team1Games) {
    team2SetsWon++;
  }

  // Check set 3 if it was played
  const set3Played = result.set3.team1Games > 0 || result.set3.team2Games > 0;
  if (set3Played) {
    if (result.set3.team1Games > result.set3.team2Games) {
      team1SetsWon++;
    } else if (result.set3.team2Games > result.set3.team1Games) {
      team2SetsWon++;
    }
  }

  // Winner needs 2 sets
  if (team1SetsWon >= 2) {
    return match.team1Id;
  } else if (team2SetsWon >= 2) {
    return match.team2Id;
  }

  return null;
}
